import { GripVertical, MoreVertical, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import invariant from 'tiny-invariant'
import { useTranslation } from '@kostbar/i18n'
import { useDeleteSection } from '../hooks/use-section-mutations'
import { MenuItemRow } from './menu-item-row'
import { StockLedgerLegend } from './stock-ledger'
import { EditSectionDialog } from './edit-section-dialog'
import { AddItemDialog } from './add-item-dialog'
import { BulkAddByCategoryDialog } from './bulk-add-by-category-dialog'
import type { MenuContents } from '@/api/types'
import { useCategories } from '@/features/categories/hooks/use-categories'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

interface MenuSectionCardProps {
  section: MenuContents['sections'][0]
  menuId: number
  restaurantId: number
  /** The menu's own day, or undefined for the master menu, which has no day to plan against. */
  menuDate?: string
}

export function MenuSectionCard({
  section,
  menuId,
  restaurantId,
  menuDate,
}: MenuSectionCardProps) {
  const { t: tMenus } = useTranslation('menus')
  // Computed once per section instead of once per item row.
  const { can } = usePermissions()
  const canReceiveStock = can(ACTIONS.INVENTORY_RECEIVE)
  const canAdjustStock = can(ACTIONS.INVENTORY_ADJUST)
  const canEditProduct = can(ACTIONS.PRODUCT_EDIT)
  const [editOpen, setEditOpen] = useState(false)
  const [addItemOpen, setAddItemOpen] = useState(false)
  const [bulkAddOpen, setBulkAddOpen] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [closestEdge, setClosestEdge] = useState<string | null>(null)
  const [isProductDragOver, setIsProductDragOver] = useState(false)

  const sectionRef = useRef<HTMLDivElement>(null)
  const dragHandleRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const deleteSection = useDeleteSection()
  const { data: categoriesData } = useCategories()
  // The location's flag, until products carry their own: it decides whether a row without an
  // inventory record reads as "sell freely" or as "the shop shows 0 and the guest cannot order".
  const { data: restaurantsData } = useRestaurants()
  const tracksInventory =
    restaurantsData?.items.find((r) => r.id === restaurantId)
      ?.inventoryTrackingEnabled !== false
  const categoryName = section.categoryId
    ? categoriesData?.items.find((c) => c.id === section.categoryId)?.name
    : undefined

  useEffect(() => {
    const sectionEl = sectionRef.current
    const handleEl = dragHandleRef.current
    const contentEl = contentRef.current
    invariant(sectionEl)
    invariant(handleEl)
    invariant(contentEl)

    return combine(
      draggable({
        element: sectionEl,
        dragHandle: handleEl,
        getInitialData: () => ({
          type: 'section',
          sectionId: section.id,
          section,
        }),
        onDragStart: () => {
          setIsDragging(true)
        },
        onDrop: () => {
          setIsDragging(false)
        },
      }),
      // Drop target for section reordering (on the whole card)
      dropTargetForElements({
        element: sectionEl,
        canDrop: ({ source }) => {
          return source.data.type === 'section'
        },
        getData: ({ input, element }) => {
          return attachClosestEdge(
            {
              type: 'section',
              sectionId: section.id,
            },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            },
          )
        },
        onDragEnter: ({ self }) => {
          const edge = extractClosestEdge(self.data)
          setClosestEdge(edge)
        },
        onDrag: ({ self }) => {
          const edge = extractClosestEdge(self.data)
          setClosestEdge(edge)
        },
        onDragLeave: () => {
          setClosestEdge(null)
        },
        onDrop: () => {
          setClosestEdge(null)
        },
      }),
      // The content area takes a new product from the panel AND an item re-filed from another
      // section (CHG-2026-09-menu-editor-redesign). Without the second kind a drop onto an empty
      // section — or onto the gap below the last row — would find no target at all.
      dropTargetForElements({
        element: contentEl,
        canDrop: ({ source }) =>
          source.data.type === 'product' ||
          (source.data.type === 'item' && source.data.sectionId !== section.id),
        getData: () => ({
          type: 'section',
          sectionId: section.id,
        }),
        onDragEnter: () => {
          setIsProductDragOver(true)
        },
        onDragLeave: () => {
          setIsProductDragOver(false)
        },
        onDrop: () => {
          setIsProductDragOver(false)
        },
      }),
    )
  }, [section.id])

  const handleDelete = () => {
    if (confirm(tMenus('sectionCard.confirmDelete', { name: section.name }))) {
      deleteSection.mutate({ menuId, sectionId: section.id })
    }
  }

  const style = {
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
  }

  return (
    <>
      <Card ref={sectionRef} style={style}>
        {closestEdge === 'top' && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary z-10" />
        )}
        {closestEdge === 'bottom' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary z-10" />
        )}
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center gap-3">
            {/* Drag handle for section */}
            <button
              ref={dragHandleRef}
              className="cursor-grab active:cursor-grabbing touch-none"
            >
              <GripVertical className="w-5 h-5 text-muted-foreground hover:text-foreground" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">{section.name}</h3>
                {section.categoryId && (
                  <Badge variant="outline" className="text-xs">
                    {tMenus('sectionCard.category')}{' '}
                    {categoryName ?? section.categoryId}
                  </Badge>
                )}
                {/* Missing menuCard reads as visible — fail open, same rule the client uses. */}
                {section.menuCard !== false && (
                  <Badge variant="secondary" className="text-xs">
                    {tMenus('sectionCard.menuCard')}
                  </Badge>
                )}
                {section.dineInOnly && (
                  <Badge variant="secondary" className="text-xs">
                    {tMenus('sectionCard.dineInOnly')}
                  </Badge>
                )}
                {section.dailySpecial && (
                  <Badge variant="secondary" className="text-xs">
                    {tMenus('sectionCard.dailySpecial')}
                  </Badge>
                )}
                {/* Neither visibility flag set: staged, not published — a section this can happen
                    to must not be mistaken for a live one. */}
                {section.menuCard === false && !section.dineInOnly && (
                  <Badge variant="destructive" className="text-xs">
                    {tMenus('sectionCard.hidden')}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {section.items.length}{' '}
                {section.items.length === 1
                  ? tMenus('sectionCard.itemSingular')
                  : tMenus('sectionCard.itemPlural')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddItemOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {tMenus('sectionCard.addItem')}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setEditOpen(true)}>
                  {tMenus('sectionCard.editSection')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setBulkAddOpen(true)}>
                  {tMenus('sectionCard.bulkAdd')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDelete}
                  className="text-destructive"
                >
                  {tMenus('sectionCard.deleteSection')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        <CardContent
          ref={contentRef}
          className={
            isProductDragOver
              ? 'border-2 border-primary border-dashed rounded-lg bg-primary/5'
              : ''
          }
        >
          {section.items.length === 0 ? (
            <div className="flex items-center justify-center py-8 rounded-lg border border-dashed">
              <p className="text-sm text-muted-foreground">
                {isProductDragOver
                  ? tMenus('sectionCard.dropHere')
                  : tMenus('sectionCard.empty')}
              </p>
            </div>
          ) : (
            // The ledger's collapse follows THIS box's width, not the viewport's — the sidebar and
            // the product panel take a few hundred pixels the viewport does not know about.
            <div className="@container">
              <StockLedgerLegend />
              <div className="space-y-1">
                {section.items.map((item) => (
                  <MenuItemRow
                    key={item.productId}
                    item={item}
                    menuId={menuId}
                    sectionId={section.id}
                    restaurantId={restaurantId}
                    menuDate={menuDate}
                    canReceiveStock={canReceiveStock}
                    canAdjustStock={canAdjustStock}
                    canEditProduct={canEditProduct}
                    tracksInventory={tracksInventory}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <EditSectionDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        menuId={menuId}
        section={section}
      />

      <AddItemDialog
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        menuId={menuId}
        sectionId={section.id}
      />

      <BulkAddByCategoryDialog
        open={bulkAddOpen}
        onOpenChange={setBulkAddOpen}
        menuId={menuId}
        sectionId={section.id}
      />
    </>
  )
}
